import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { formatStat, formatDecimalWithCommas } from '../../utils/format.utils';
import { buildConfigurableTooltipFormatter } from '../../utils/chart.utils';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { Subject, Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption, getDoubleAxisGridAdjustments } from '../../utils/axis.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, DataErrorComponent],
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrl: './horizontal-bar-chart.component.scss',
})
export class HorizontalBarChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  protected myChart!: echarts.ECharts;
  protected allCategories: string[] = [];
  public hasError = false;
  public errorMessage = '';

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  protected configService = inject(ConfigService);
  protected globalFilterService = inject(GlobalFilterService);
  protected chartFilterHandler = inject(ChartFilterHandlerService);
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  protected themeSub?: Subscription;
  protected unSubscribe$: Subject<void> = new Subject<void>();
  protected currentFilters: any;
  protected selectedCategory: string | null = null;
  private lastChartData?: { category: string[]; value: number[] };

  constructor(
    configService?: ConfigService,
    globalFilterService?: GlobalFilterService,
    chartFilterHandler?: ChartFilterHandlerService,
  ) {
    if (configService) this.configService = configService;
    if (globalFilterService) this.globalFilterService = globalFilterService;
    if (chartFilterHandler) this.chartFilterHandler = chartFilterHandler;
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => this.allCategories,
      // Custom highlight: horizontal bar uses color swap (not opacity)
      onHighlightCategories: (cats) => this.applyCategoryHighlight(cats),
      onResetCategoryHighlight: () => this.resetCategoryHighlight(),
    });

    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init is required because ECharts' registered 'dvm' theme
    // updates do NOT propagate to already-initialized chart instances.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    this.configService
      .getConfig(this.data.apiUrl, this.data, {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false,
      })
      ?.subscribe({
        next: (res: any) => {
          if (!res) return;
          const dataKey = this.data?.dataKey;
          const raw = dataKey ? res?.[dataKey] : (res?.data || res);
          if (Array.isArray(raw)) {
            this.wrapper.setSourceData(raw, this.data?.categoryKey || 'category');
          }
          let chartData: any;
          if (Array.isArray(raw)) {
            const catKey = this.data?.categoryKey || 'category';
            const valKey = this.data?.valueKey || 'value';
            chartData = {
              category: raw.map((item: any) => item[catKey]),
              value: raw.map((item: any) => item[valKey]),
            };
          } else {
            chartData = raw;
          }
          const isEmpty = !chartData || (Array.isArray(chartData) && chartData.length === 0);

          if (isEmpty) {
            this.hasError = true;
            this.errorMessage = 'No data available for the selected filters.';
            this.myChart.clear();
          } else {
            this.hasError = false;
            this.renderChart(chartData);
            this.addResizeListener();
          }

          this.myChart.hideLoading();
        },
        error: () => {
          this.hasError = true;
          this.errorMessage = 'No Data available for the chart.';
          this.myChart.clear();
          this.myChart.hideLoading();
        },
      });
  }

  protected renderChart(chartData?: { category: string[]; value: number[] }): void {
    if (!this.data) return;

    if (chartData) this.lastChartData = chartData;

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    this.allCategories = chartData?.category || [];

    const extendLeft = this.data.orientation === 'left';
    const categories = chartData?.category || [];
    const gridAdj = getDoubleAxisGridAdjustments(this.data);

    const primaryYAxis: any = {
      type: 'category',
      name: 'Engine Model',
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: { fontWeight: 'bold', fontSize: 14 },
      data: categories,
      triggerEvent: true,
      axisLabel: {
        formatter: (value: string) => (value.length > 10 ? value.slice(0, 10) + '…' : value),
      },
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const primaryXAxis: any = {
      type: 'value',
      inverse: extendLeft,
      axisLabel: { show: false },
      splitLine: { show: false },
    };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    // Determine which categories are selected for initial render coloring
    const selectedCats = this.wrapper.selectedCategories;

    const option = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        axisPointer: { type: 'shadow' },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          const configurableFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
          if (configurableFormatter) return configurableFormatter;

          return (params: any) => {
            const data = params[0];
            const category = data?.name || '';
            const value = data?.value || 0;
            const tooltipLabels = this.data?.tooltipLabels || {};
            const categoryLabel = tooltipLabels.category || 'Category';
            const valueLabel = tooltipLabels.value || 'Value';
            const formattedValue = this.data?.useCommaStat ? formatDecimalWithCommas(value) : value;
            return `<div style="text-align:left;"><div><b>${categoryLabel}:</b> ${category}</div><div><b>${valueLabel}:</b> ${formattedValue}</div></div>`;
          };
        })(),
      },
      grid: {
        left: 40,
        right: `${40 + gridAdj.extraRight}px`,
        top: 5,
        bottom: 10 + gridAdj.extraBottom,
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: [
        {
          type: 'bar',
          data:
            chartData?.category.map((cat, index) => {
              const value = chartData?.value[index];
              const isSelected = selectedCats.length === 0 || selectedCats.includes(cat);

              return {
                value,
                itemStyle: {
                  color: isSelected ? this.data.barColor || theme.chart?.palette?.[1] || '#91CC75' : 'rgba(200, 200, 200, 0.3)',
                },
                label: {
                  show: true,
                  offset: [2, 6],
                  position: (params: any) => {
                    const val = params.value;
                    return val < 0 ? 'insideLeft' : 'insideRight';
                  },
                  color: theme.chart?.textColor || 'black',
                  formatter: ({ value }: any) => formatStat(value),
                },
              };
            }) || [],
          emphasis: {
            label: { show: true },
          },
        },
      ],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    setTimeout(() => this.myChart.hideLoading(), 1000);

    // Wrapper wires click events — but we override with custom click for horizontal bar
    this.wrapper.afterRender();
    this.setupCustomClickEvent();
  }

  // Custom click: horizontal bar has y-axis for categories (inverted from standard)
  protected setupCustomClickEvent(): void {
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params.componentType === 'yAxis') {
        const axisIndex = params.yAxisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleYAxis && this.data?.enableYAxis2Click) {
          this.chartFilterHandler.handleDoubleYAxisClick(this.data, params.value);
          this.wrapper.highlightByGroup(params.value);
        } else if (this.data?.enableLabelClick) {
          this.wrapper.handleLabelClick(params.value);
        }
      } else if (params.componentType === 'xAxis') {
        const axisIndex = params.xAxisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleXAxis && this.data?.enableXAxis2Click) {
          this.chartFilterHandler.handleDoubleXAxisClick(this.data, params.value);
          this.wrapper.highlightByGroup(params.value);
        }
      } else {
        const clickedCategory = params?.name;
        if (!clickedCategory) return;

        if (this.data?.enableLabelClick) {
          this.wrapper.handleLabelClick(clickedCategory);
        } else {
          this.chartFilterHandler.updateFilterSelection(this.data, clickedCategory);
        }
      }
    });
  }

  protected setupChartClickEvent(): void {
    this.setupCustomClickEvent();
  }

  // Custom highlight: color-based (not opacity-based)
  protected applyCategoryHighlight(categories?: string[]): void {
    if (!this.myChart) return;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const option = this.myChart.getOption() as any;
    if (!option?.series?.[0]?.data) return;

    const cats = categories || this.wrapper.selectedCategories;
    const updatedData = this.allCategories.map((cat, index) => {
      const original = option.series[0].data[index];
      const isSelected = cats.length === 0 || cats.includes(cat);
      return {
        ...original,
        itemStyle: {
          ...(original?.itemStyle || {}),
          color: isSelected ? this.data.barColor || theme.chart?.palette?.[1] || '#91CC75' : 'rgba(200, 200, 200, 0.3)',
        },
      };
    });

    this.myChart.setOption({ series: [{ data: updatedData }] });
  }

  protected resetCategoryHighlight(): void {
    if (!this.myChart) return;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const option = this.myChart.getOption() as any;
    if (!option?.series?.[0]?.data) return;

    const updatedData = option.series[0].data.map((item: any) => ({
      ...item,
      itemStyle: {
        ...(item?.itemStyle || {}),
        color: this.data.barColor || theme.chart?.palette?.[1] || '#91CC75',
      },
    }));

    this.myChart.setOption({ series: [{ data: updatedData }] });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
